/**
 * GroupRow Component
 *
 * Renders a collapsible group header row for grouping.
 * Shows group totals for market value (USD) and unrealized P&L (USD).
 */

import { ChevronDown } from "lucide-react";
import React from "react";
import { GroupByOption } from "./TableControls";

export interface GroupRowProps {
  /** Group identifier (e.g., currency code, timezone, sector) */
  groupKey: string;
  /** Current grouping mode */
  groupBy: GroupByOption;
  /** Aggregated totals for the group (in USD for consistency) */
  totals: {
    mktValueUsd: number;
    unrealizedPnlUsd: number;
    count: number;
  };
  /** Whether group is expanded to show positions */
  isExpanded: boolean;
  /** Callback to toggle expand/collapse */
  onToggle: () => void;
  /** Total number of columns for colspan */
  columnCount: number;
  isDarkMode: boolean;
}

/**
 * Get the display label for the grouping type
 */
function getGroupLabel(groupBy: GroupByOption): string {
  switch (groupBy) {
    case "currency":
      return "Currency";
    case "sector":
      return "Sector";
    case "group":
      return "Group";
    case "secType":
      return "Type";
    case "timezone":
      return "Timezone";
    default:
      return "";
  }
}

const GroupRow: React.FC<GroupRowProps> = ({
  groupKey,
  groupBy,
  totals,
  isExpanded,
  onToggle,
  columnCount,
  isDarkMode,
}) => {
  const formatUsd = (value: number): string => {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const groupLabel = getGroupLabel(groupBy);

  return (
    <tr
      onClick={onToggle}
      className={`cursor-pointer ${
        isDarkMode ? "bg-gray-700 hover:bg-gray-650" : "bg-gray-100 hover:bg-gray-200"
      } transition-colors`}
    >
      <td
        colSpan={columnCount}
        className={`px-2 py-2 font-bold border ${
          isDarkMode ? "border-gray-600 text-white" : "border-gray-300 text-gray-900"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "" : "-rotate-90"}`} />
            <span>
              {groupLabel}: {groupKey}
            </span>
            <span className={`text-xs font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              ({totals.count} positions)
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
              Mkt Value (USD): {formatUsd(totals.mktValueUsd)}
            </span>
            <span className={totals.unrealizedPnlUsd >= 0 ? "text-green-400" : "text-red-400"}>
              Unrealized P&L (USD): {totals.unrealizedPnlUsd >= 0 ? "+" : ""}
              {formatUsd(Math.abs(totals.unrealizedPnlUsd))}
            </span>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default GroupRow;
